import type { Service, ServiceCategory } from './types';

/**
 * The catalogue is fetched in one page and grouped in the browser.
 *
 * 100 is the API's own ceiling on `limit`. A nail salon runs a few dozen
 * services, so one request covers it; the page tells the user outright when the
 * total exceeds what it received rather than quietly dropping the tail.
 */
export const CATALOGUE_LIMIT = 100;

/** Sentinel group for services filed under no category. */
export const UNCATEGORIZED = 'uncategorized' as const;

export type GroupKey = number | typeof UNCATEGORIZED;

export interface ServiceGroup {
  key: GroupKey;
  label: string;
  color: string | null;
  /** Null for the uncategorized group, which is not an editable row. */
  category: ServiceCategory | null;
  services: Service[];
}

/**
 * Lowercases and strips diacritics — the browser-side twin of the API's
 * `fold()`.
 *
 * Same reason it exists there: somebody looking for "Diseño" types "diseno",
 * and an accent that survives the comparison makes the filter answer "nothing
 * found" over a catalogue that plainly contains it.
 */
export const fold = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** Whether a service matches the filter box. Empty term matches everything. */
export const matchesSearch = (service: Service, term: string): boolean => {
  const needle = fold(term.trim());
  if (!needle) return true;
  return fold(`${service.name} ${service.description ?? ''}`).includes(needle);
};

/**
 * Chair time as a person says it: `90` reads "1 h 30 min", not "90 min".
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  if (rest === 0) return `${hours} h`;
  return `${hours} h ${rest} min`;
};

/**
 * Buckets services into their categories, in the order the business chose.
 *
 * Every category appears even when empty — an empty group is the affordance
 * that tells the user where a new service would land, and it is also the only
 * place the "delete this category" action can live.
 *
 * Services whose category was filtered out of the fetched page (or that have
 * none) collect in a trailing group rather than disappearing.
 */
export const groupByCategory = (
  services: Service[],
  categories: ServiceCategory[]
): ServiceGroup[] => {
  const groups = new Map<GroupKey, ServiceGroup>();

  for (const category of categories) {
    groups.set(category.id, {
      key: category.id,
      label: category.name,
      color: category.color,
      category,
      services: [],
    });
  }

  const orphans: Service[] = [];
  for (const service of services) {
    const group = service.categoryId === null ? undefined : groups.get(service.categoryId);
    if (group) group.services.push(service);
    else orphans.push(service);
  }

  const ordered = [...groups.values()];
  if (orphans.length > 0) {
    ordered.push({
      key: UNCATEGORIZED,
      label: 'Sin categoría',
      color: null,
      category: null,
      services: orphans,
    });
  }
  return ordered;
};
